import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { billingApi } from '../../api/billing.js';
import { exportApi } from '../../api/export.js';
import { pipelineApi } from '../../api/pipeline.js';
import { pushQueueApi } from '../../api/pushQueue.js';
import { MessageBanner } from '../../components/ui';
import { useEffectiveDateRange } from '../../hooks';
import { useUrlDebouncedParam, useUrlFlag, useUrlParam, useUrlSet, useUrlState } from '../../url';
import {
  BULK_ACTION_BY_STATUS, OPS_REVIEW_STATUSES, PAGE_SIZE, STATUS_RETURN_LABEL,
  TAB_DEFAULT_SORT,
} from './constants.js';
import { PushQueuePanel } from './PushQueuePanel.jsx';
import { RecordsFilters } from './RecordsFilters.jsx';
import { RecordsTable } from './RecordsTable.jsx';
import { RecordsTabs } from './RecordsTabs.jsx';

export default function Records() {
  // Data
  const [records,  setRecords]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [hasMore,  setHasMore]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // True while ANY user-initiated mutation (Approve / Push / Void /
  // Re-push / Retry / Acknowledge) is in flight against the API. Drives
  // button "Working..." labels so the user has visible feedback.
  const [mutating, setMutating] = useState(null);   // null | label string

  const { setParams } = useUrlState();

  // Tab + every filter live in the URL, so the address bar is the whole view:
  // paste it to a colleague and they land on the same tab, the same narrowing,
  // the same sort, with the same row expanded. Nothing on this page keeps a
  // second copy of that state.
  const [filter] = useUrlParam('filter', 'all');
  // Search keeps a local draft so typing stays instant; only the settled value
  // reaches the URL (and therefore the API).
  const [search, setSearch, committedSearch] = useUrlDebouncedParam('q');
  const [dateStart, setDateStart] = useUrlParam('date_start');
  const [dateEnd,   setDateEnd]   = useUrlParam('date_end');
  const [siteCode,        setSiteCode]        = useUrlParam('site_code');
  const [billingTitleId,  setBillingTitleId]  = useUrlParam('billing_title_id');
  const [payGroup,        setPayGroup]        = useUrlParam('pay_group');
  const [customerOt,      setCustomerOt]      = useUrlParam('customer_ot');
  const [statusAll,       setStatusAll]       = useUrlParam('status_all');
  const [gapTypes,        setGapTypes, gapTypesSig] = useUrlSet('gap_types');
  const [exclusionReason, setExclusionReason] = useUrlParam('exclusion_reason');
  const [pushEligibleOnly, setPushEligibleOnly] = useUrlFlag('push_eligible');

  // Sort. Absent from the URL means "this tab's default", so a shared link
  // only carries a sort when the user actually chose one.
  const [sortByParam]  = useUrlParam('sort_by');
  const [sortDirParam] = useUrlParam('sort_dir');
  const tabSort = TAB_DEFAULT_SORT[filter] || TAB_DEFAULT_SORT.all;
  const sortBy  = sortByParam  || tabSort.by;
  const sortDir = sortDirParam || tabSort.dir;
  // One setter for the pair: column and direction always move together, and a
  // null pair means "back to the tab default".
  const setSort = useCallback(
    (by, dir) => setParams({ sort_by: by || null, sort_dir: dir || null }),
    [setParams],
  );

  // The expanded row, carried in the URL as its DURABLE record_key — not its
  // id. Ids are rebuilt by every reprocess, so "here is the filtered list with
  // this row open" would open the wrong row (or none) by morning.
  const [expandedId, setExpandedId] = useUrlParam('open', '', { replace: false });

  // UI state
  const [facets,        setFacets]        = useState(null);
  // True while facets (gap-type counts, location/title options) are being
  // re-fetched. Drives hiding the stale gap chips until fresh counts arrive.
  const [facetsLoading, setFacetsLoading] = useState(false);
  // ``selected`` holds the ticked record ids — EXCEPT while
  // ``selectAllMatching`` is on, when it flips meaning and holds the rows the
  // user has UN-ticked out of the whole filter. One Set, two modes; the
  // effective count is ``selectionCount`` below and nothing else should do
  // that arithmetic itself.
  const [selected,    setSelected]    = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [msg,         setMsg]         = useState(null);
  const [detailCache, setDetailCache] = useState({});
  // Tracks whether something NS-touching is in flight (ns_extractor /
  // ns_loader / pl-extract-netsuite). When true, the "Push to NS" button is
  // disabled to avoid double-loading the NetSuite REST API.
  const [nsConflict,  setNsConflict]  = useState(null);

  // The Sync Queue is a tab like any other — derived, never a second copy.
  const showQueue = filter === 'push_queue';

  // Debounced + validated date range (see useEffectiveDateRange): <input
  // type="date"> fires per keystroke, so typing a year walks through absurd
  // partial values that spammed + 500'd the backend. Only fire once the date
  // is complete and sane.
  const [effDateStart, effDateEnd] = useEffectiveDateRange(dateStart, dateEnd);

  const offsetRef        = useRef(0);
  const sentinelRef      = useRef(null);
  const fetchingRef      = useRef(false);
  const versionRef       = useRef(0);
  const fetchFnRef       = useRef(null);
  const facetsVersionRef = useRef(0);
  // Record ids whose detail fetch has already been attempted this filter.
  const detailFetchedRef = useRef(new Set());

  // Every mutation ends by re-reading the list. The filter itself hasn't
  // changed — this counter is in the fetch effect's deps purely to say
  // "the rows behind the current filter moved, go and look again".
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshList = useCallback(() => setRefreshTick((n) => n + 1), []);

  // THE filter payload. One builder feeds both the table query and any
  // "select all in filter" bulk action, so the records a bulk action touches
  // are provably the records the table was showing — a second, drifting copy
  // of this logic is how a mass action ends up hitting rows nobody saw.
  // Paging and sort are deliberately NOT here: they change what you SEE, never
  // which records MATCH.
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (filter === 'ops_review') {
      params.status = OPS_REVIEW_STATUSES.join(',');
    } else if (filter && filter !== 'all' && filter !== 'push_queue') {
      params.status = filter;
    } else if (filter === 'all' && statusAll) {
      params.status = statusAll;
    } else if (filter === 'all') {
      // Excluded-ref-data records have their own tab + drill-down; keep
      // them out of the All view so totals + scrolling reflect actionable
      // billing records only.
      params.exclude_status = 'excluded_ref_data';
    }
    if (committedSearch)  params.search       = committedSearch;
    if (effDateStart)     params.date_start   = effDateStart;
    if (effDateEnd)       params.date_end     = effDateEnd;
    if (siteCode)         params.site_code    = siteCode;
    if (billingTitleId)   params.billing_title_id = billingTitleId;
    if (payGroup)         params.pay_group    = payGroup;
    if (customerOt)       params.is_customer_ot = customerOt;
    if (gapTypes.size > 0) params.gap_types   = Array.from(gapTypes).join(',');
    if (filter === 'excluded_ref_data' && exclusionReason) {
      params.exclusion_reason = exclusionReason;
    }
    if (filter === 'pending' && pushEligibleOnly) {
      params.has_billable_lines = 'true';
    }
    return params;
  }, [
    filter, committedSearch, effDateStart, effDateEnd, siteCode, billingTitleId, payGroup,
    customerOt, statusAll, gapTypes, exclusionReason, pushEligibleOnly,
  ]);

  fetchFnRef.current = useCallback((offset, limit) => {
    const params = { ...buildFilterParams(), limit, offset };
    if (sortBy)  params.sort_by  = sortBy;
    if (sortDir) params.sort_dir = sortDir;
    return billingApi.list(params);
  }, [buildFilterParams, sortBy, sortDir]);

  // Reset (reload from offset 0) when any filter changes.
  // Using ``gapTypesSig`` instead of the Set keeps the deps stable.
  useEffect(() => {
    if (filter === 'push_queue') return;
    const version = ++versionRef.current;
    fetchingRef.current = true;
    setLoading(true);
    offsetRef.current = 0;
    fetchFnRef.current(0, PAGE_SIZE)
      .then((r) => {
        if (versionRef.current !== version) { fetchingRef.current = false; return; }
        setRecords(r.items);
        if (r.total != null) setTotal(r.total);
        setHasMore(r.has_more || false);
        offsetRef.current = r.items.length;
        // Drop "select all matching" along with the ticks. The flag is bound to
        // the filter that produced it — carrying it across a filter change
        // would silently re-aim a pending bulk action at a different set of
        // records than the user agreed to.
        setSelectAllMatching(false);
        setSelected(new Set());
        // Drop the cached detail payloads — the rows behind them just changed.
        // ``expandedId`` is deliberately left alone: it lives in the URL and a
        // shared link is allowed to arrive with both a filter and an open row.
        setDetailCache({});
        detailFetchedRef.current = new Set();
        setLoading(false);
        fetchingRef.current = false;
      })
      .catch((e) => {
        if (versionRef.current !== version) { fetchingRef.current = false; return; }
        setMsg({ type: 'error', text: e.message });
        setLoading(false);
        fetchingRef.current = false;
      });
  }, [
    filter, committedSearch, effDateStart, effDateEnd, siteCode, billingTitleId, payGroup,
    customerOt, statusAll, gapTypesSig, exclusionReason, pushEligibleOnly,
    sortBy, sortDir, refreshTick,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facets refresh when tab + date range change. Versioned so a slow stale
  // response can't overwrite a newer one, and ``facetsLoading`` stays true only
  // until the latest request resolves (drives hiding the stale gap chips).
  useEffect(() => {
    if (filter === 'push_queue') return;
    const facetParams = {};
    if (filter === 'ops_review') {
      facetParams.status = OPS_REVIEW_STATUSES.join(',');
    } else if (filter && filter !== 'all') {
      facetParams.status = filter;
    }
    if (effDateStart) facetParams.date_start = effDateStart;
    if (effDateEnd)   facetParams.date_end = effDateEnd;
    const v = ++facetsVersionRef.current;
    setFacetsLoading(true);
    billingApi.facets(facetParams)
      .then((f) => { if (facetsVersionRef.current === v) setFacets(f); })
      .catch(() => {})
      .finally(() => { if (facetsVersionRef.current === v) setFacetsLoading(false); });
  }, [filter, effDateStart, effDateEnd]);

  // Drop the success/error banner on a tab change — it's tab-scoped feedback
  // ("2 records approved" shouldn't follow the user to the Approved tab and
  // require a manual dismiss). Selection is cleared too so a stale checkbox
  // set from the previous tab doesn't leak into the next tab's action bar.
  //
  // The tab-scoped FILTERS are cleared by handleTabClick instead of here: they
  // live in the URL now, and an effect that wipes them would also wipe the
  // ones a shared link arrived with.
  useEffect(() => {
    setMsg(null);
    setSelectAllMatching(false);
    setSelected(new Set());
  }, [filter]);

  const loadMore = useCallback(async () => {
    if (fetchingRef.current || !hasMore) return;
    const version = versionRef.current;
    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const r = await fetchFnRef.current(offsetRef.current, PAGE_SIZE);
      if (versionRef.current !== version) { fetchingRef.current = false; return; }
      setRecords((prev) => [...prev, ...r.items]);
      setHasMore(r.has_more || false);
      offsetRef.current += r.items.length;
    } catch (e) {
      if (versionRef.current !== version) { fetchingRef.current = false; return; }
      setMsg({ type: 'error', text: e.message });
    }
    setLoadingMore(false);
    fetchingRef.current = false;
  }, [hasMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) loadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading]);

  // Push Queue panel is now self-managing — it loads + filters its own
  // data internally. No shared loadQueue() helper needed.

  // Poll /api/pipeline/active every 15 s to know when NS is busy. Only the
  // ``touches_netsuite`` flag matters here (Dayforce extracts and reprocess
  // are fine — they don't burden the NS API). Mount + interval; cleared on
  // unmount.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await pipelineApi.getActive();
        if (cancelled) return;
        const c = res?.conflict;
        setNsConflict(c && c.touches_netsuite ? c : null);
      } catch {
        // best-effort; transient failures shouldn't disable the button
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Load the expanded row's detail. Driven by the URL rather than by the click
  // handler, so `?open=12345` on a pasted link opens the same panel the person
  // who sent it was looking at. ``detailFetchedRef`` stops a record whose fetch
  // failed from being retried on every render.
  useEffect(() => {
    if (!expandedId) return undefined;
    // ``open`` carries the durable key, so map it back to the loaded row to get
    // today's id — the detail endpoint and the cache are both id-keyed, and the
    // id is only meaningful for as long as this list is on screen anyway. An
    // id-bearing link from before this change still matches on the second test.
    const row = records.find(
      (r) => String(r.record_key) === String(expandedId)
        || String(r.id) === String(expandedId),
    );
    if (!row) return undefined;
    if (detailCache[row.id] || detailFetchedRef.current.has(row.id)) return undefined;
    detailFetchedRef.current.add(row.id);
    let cancelled = false;
    billingApi.get(row.id)
      .then((rec) => { if (!cancelled) setDetailCache((prev) => ({ ...prev, [row.id]: rec })); })
      .catch((e) => { if (!cancelled) setMsg({ type: 'error', text: e.message }); });
    return () => { cancelled = true; };
  }, [expandedId, detailCache, records]);

  // In filter mode a tick/un-tick moves the row in or out of the EXCLUSION
  // set, so the checkbox still reads the way the user expects.
  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const clearSelection = () => {
    setSelectAllMatching(false);
    setSelected(new Set());
  };

  const selectAll = () => {
    if (selectAllMatching) { clearSelection(); return; }
    if (selected.size === records.length) setSelected(new Set());
    else setSelected(new Set(records.map((r) => r.id)));
  };

  // "Select all N matching this filter" — the whole point of the feature: act
  // on every record the filter returns, not just the ~100 rows rendered.
  const selectAllMatchingFilter = () => {
    setSelectAllMatching(true);
    setSelected(new Set());     // now an exclusion set, and nothing is excluded
  };

  const selectionCount = selectAllMatching
    ? Math.max(0, total - selected.size)
    : selected.size;

  // What every bulk call sends: ticked ids, or the filter + the un-ticked rows.
  const selectionPayload = () => (selectAllMatching
    ? { filter: buildFilterParams(), exclude_ids: [...selected] }
    : { record_ids: [...selected] });

  // CSV of what the table is showing — resolved server-side, so it is never
  // the ~100 rows the browser has scrolled into. The three cases the user can
  // be in, and what each means:
  //
  //   "select all N matching"  -> every matching record minus the un-ticked
  //   rows ticked              -> exactly those rows, even ones scrolled past
  //   nothing ticked           -> the whole filter
  //
  // Sort rides along so the file opens in the order the table was in.
  const exportRecords = () => {
    const body = {
      params: { ...buildFilterParams(), sort_by: sortBy, sort_dir: sortDir },
    };
    if (selectAllMatching) body.exclude_ids = [...selected];
    else if (selected.size > 0) body.record_ids = [...selected];
    return exportApi.download('billing_records', body);
  };

  // Say what the button will actually export — "Export CSV" over a 3,400-row
  // selection is the kind of vagueness that gets a wrong file emailed out.
  const exportLabel = selectionCount > 0
    ? `Export ${selectionCount.toLocaleString()} selected`
    : 'Export CSV';

  // Filter mode acts on records the user cannot see, so before every action we
  // ask the backend what it would ACTUALLY touch and put those numbers in the
  // dialog. Returns null when the action can't or shouldn't run.
  const bulkGate = async (action, label) => {
    if (!selectAllMatching) {
      return { count: selected.size, matched: selected.size, skipped: 0 };
    }
    let p;
    try {
      p = await billingApi.bulkPreview(action, buildFilterParams(), [...selected]);
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
      return null;
    }
    if (p.over_cap) {
      setMsg({
        type: 'error',
        text: `${label} can run on at most ${p.cap.toLocaleString()} records at a time, `
          + `and ${p.matched.toLocaleString()} match this filter. Narrow it `
          + '(add a date range or a location) and try again.',
      });
      return null;
    }
    if (!p.eligible) {
      setMsg({
        type: 'error',
        text: `None of the ${p.matched.toLocaleString()} records matching this filter `
          + `are eligible for ${label}.`,
      });
      return null;
    }
    return { count: p.eligible, matched: p.matched, skipped: p.skipped };
  };

  // Confirm text for a filter-wide action. Always names the eligible count —
  // the number the backend will actually move — and says what gets left behind.
  const bulkPrompt = (label, p, tail = '') =>
    `${label} ${p.count.toLocaleString()} of the ${p.matched.toLocaleString()} records `
    + 'matching this filter?'
    + (p.skipped
      ? `\n\n${p.skipped.toLocaleString()} will be skipped — they aren't in an eligible status.`
      : '')
    + (tail ? `\n\n${tail}` : '');

  // Wrapper that gates an async action behind the global ``mutating``
  // state so the action bar can show "Working..." and disable buttons.
  const runMutation = async (label, fn) => {
    if (mutating) return;     // already running another action
    setMutating(label);
    try {
      await fn();
    } finally {
      setMutating(null);
    }
  };

  const updateStatus = (status) => runMutation(
    status === 'approved' ? 'Approving' : `Setting ${status}`,
    async () => {
      if (selectionCount === 0) return;
      const label = status === 'approved' ? 'Approve' : `Set ${status}`;
      const p = await bulkGate(BULK_ACTION_BY_STATUS[status], label);
      if (!p) return;
      // Only filter mode confirms: the ticked-rows path is unchanged, and
      // adding a dialog to the everyday click would just train people past it.
      if (selectAllMatching && !window.confirm(bulkPrompt(label, p))) return;
      try {
        const res = await billingApi.updateStatus(selectionPayload(), status);
        // Two independent skip reasons, reported separately because they need
        // different fixes: wrong source status (someone else moved the record)
        // vs nothing billable on it.
        const skippedStatus = selectAllMatching ? 0 : (res.skipped || 0);
        setMsg({
          type: 'success',
          text: `${res.updated.toLocaleString()} records ${status}`
            + (res.skipped_unpushable
              ? ` — ${res.skipped_unpushable.toLocaleString()} skipped (no billable lines).`
              : '')
            + (skippedStatus
              ? ` — ${skippedStatus.toLocaleString()} skipped (no longer in an eligible status).`
              : ''),
        });
        refreshList();
        clearSelection();
      } catch (e) {
        setMsg({ type: 'error', text: e.message });
      }
    },
  );

  const voidSelected = () => runMutation('Voiding in NetSuite', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('void', 'Void');
    if (!p) return;
    const tail = 'This deletes every NetSuite TimeBill for the selected punches '
      + 'and returns them to Pending.'
      // One NS DELETE per TimeBill at 2-4s, in a background thread with no
      // queue behind it — say how long before they commit, not after.
      + (p.count > 25 ? `\n\nExpect ~${Math.ceil(p.count * 3 / 60)} min of background work.` : '');
    if (!window.confirm(selectAllMatching
      ? bulkPrompt('Void', p, tail)
      : `Void ${p.count} record${p.count === 1 ? '' : 's'}? ${tail}`)) return;
    try {
      const res = await billingApi.voidRecords(selectionPayload());
      // Backend now fires the void asynchronously in a daemon thread to
      // avoid the 230s Azure gateway timeout on bulk operations. The
      // response only confirms the work started; user refreshes to see
      // results trickle in.
      setMsg({
        type: 'success',
        text: res.message
          || `Voiding ${res.void_started} record${res.void_started === 1 ? '' : 's'} in NetSuite — refresh in a moment for status.`,
      });
      // Wait briefly so the first few voids land before the refetch,
      // then refresh. The user can manually refresh again to track progress.
      clearSelection();
      setTimeout(() => {
        refreshList();
      }, 4000);
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  const retrySelected = () => runMutation('Retrying sync', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('retry', 'Retry');
    if (!p) return;
    const tail = 'Each will be re-synced to NetSuite.';
    if (!window.confirm(selectAllMatching
      ? bulkPrompt('Retry', p, tail)
      : `Retry ${p.count} record${p.count === 1 ? '' : 's'}? ${tail}`)) return;
    try {
      const res = await billingApi.retryRecords(selectionPayload());
      setMsg({
        type: 'success',
        text: `${res.reopened.toLocaleString()} record${res.reopened === 1 ? '' : 's'} re-queued (${res.queued_lines.toLocaleString()} sync rows). Sync runs immediately in the background.`,
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  const repushSelected = () => runMutation('Re-syncing in NetSuite', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('repush', 'Re-sync');
    if (!p) return;
    const tail = 'Existing NetSuite TimeBills will be voided and replaced with '
      + 'the current line items.'
      + (p.count > 25 ? `\n\nExpect ~${Math.ceil(p.count * 3 / 60)} min of background work.` : '');
    if (!window.confirm(selectAllMatching
      ? bulkPrompt('Re-sync', p, tail)
      : `Re-sync ${p.count} record${p.count === 1 ? '' : 's'}? ${tail}`)) return;
    try {
      const res = await billingApi.repushRecords(selectionPayload());
      // Over ~25 records the backend runs the loop in a background thread and
      // returns {started} with no per-record results — there is nothing to
      // report yet, so say what's happening instead of "0 re-synced".
      if (res.started) {
        setMsg({
          type: 'success',
          text: res.message
            || `Re-syncing ${res.started.toLocaleString()} records in NetSuite — refresh in a moment for status.`,
        });
        clearSelection();
        setTimeout(() => { refreshList(); }, 4000);
        return;
      }
      const fails = (res.results || []).filter((r) => !r.success);
      setMsg({
        type: fails.length ? 'error' : 'success',
        text: fails.length
          ? `${res.repushed} re-synced; ${fails.length} failed. Check NetSuite for any partials.`
          : `${res.repushed} record${res.repushed === 1 ? '' : 's'} re-queued. New TimeBills will be created in NetSuite shortly.`,
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  // Acknowledge is the one action with no bulk endpoint — it loops one call
  // per id, so it stays checkbox-only. (No tab currently offers it; the button
  // was retired 2026-05-09 in favour of Void / Re-sync / Mark Handled.)
  const acknowledgeSelected = () => runMutation('Acknowledging', async () => {
    if (selected.size === 0 || selectAllMatching) return;
    if (!window.confirm(
      `Acknowledge ${selected.size} record${selected.size === 1 ? '' : 's'}? They will leave the Ops Review queue.`,
    )) return;
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      try {
        await billingApi.acknowledge(id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setMsg({
      type: fail ? 'error' : 'success',
      text: fail
        ? `${ok} acknowledged, ${fail} failed.`
        : `${ok} record${ok === 1 ? '' : 's'} acknowledged.`,
    });
    refreshList();
    setSelected(new Set());
  });

  // Manually Handled — the punch was billed outside this app (manual NS
  // entry, credit note, out-of-band invoice). Terminal: the reprocess apply
  // step re-stamps it last, so no nightly pass ever pulls the record back
  // into Pending or Ops Review. The optional note is persisted to
  // billing.record_state and therefore survives the reprocess wipe too.
  const markHandledSelected = () => runMutation('Marking handled', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('mark_handled', 'Mark Handled');
    if (!p) return;
    const head = selectAllMatching
      ? bulkPrompt('Mark Handled for', p)
      : `Mark ${p.count} record${p.count === 1 ? '' : 's'} as Manually Handled?`;
    const note = window.prompt(
      `${head}\n\n`
      + 'They leave this queue for good — no reprocess, drift check or Ops '
      + 'Review will move them again. Use this only when the punch has been '
      + 'billed outside the app. (Undo Handling sends them back here if you '
      + 'change your mind.)\n\n'
      + 'Optional note (why / where it was billed):',
      '',
    );
    if (note === null) return;      // Cancel
    try {
      const res = await billingApi.markManuallyHandled(selectionPayload(), note || null);
      const skipped = res.skipped_ineligible || 0;
      setMsg({
        type: skipped ? 'error' : 'success',
        text: `${res.updated.toLocaleString()} record${res.updated === 1 ? '' : 's'} marked manually handled`
          + (skipped
            ? ` — ${skipped.toLocaleString()} skipped (only Pending and Ops Review records can be marked; void a synced record first).`
            : '.'),
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  // Undo returns each record to the status it came FROM, not blanket-Pending:
  // an Ops Review record still has a live NetSuite TimeBill, and Pending is one
  // Approve + Sync click away from duplicating it.
  const unhandleSelected = () => runMutation('Undoing', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('unhandle', 'Undo Handling');
    if (!p) return;
    const tail = 'Each goes back to where it came from — Pending records to Pending, '
      + 'Ops Review records to Ops Review. The next reprocess then re-derives '
      + 'their real status.';
    if (!window.confirm(selectAllMatching
      ? bulkPrompt('Undo Manually Handled for', p, tail)
      : `Undo Manually Handled for ${p.count} record${p.count === 1 ? '' : 's'}? ${tail}`)) return;
    try {
      const res = await billingApi.unmarkManuallyHandled(selectionPayload());
      const breakdown = Object.entries(res.by_status || {})
        .filter(([, n]) => n > 0)
        .map(([s, n]) => `${n} → ${STATUS_RETURN_LABEL[s] || s}`)
        .join(', ');
      setMsg({
        type: 'success',
        text: `${res.updated.toLocaleString()} record${res.updated === 1 ? '' : 's'} un-handled`
          + (breakdown ? ` (${breakdown}).` : '.'),
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  // Manually Excluded — ops decides the punch shouldn't be billed at all, for
  // a reason the engine can't know (duplicate entry, goodwill, internal work
  // miscoded). Terminal like Mark Handled: apply pass 9 re-stamps it, so the
  // record never drifts back into Pending or Gaps.
  const markExcludedSelected = () => runMutation('Excluding', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('mark_excluded', 'Mark Excluded');
    if (!p) return;
    const head = selectAllMatching
      ? bulkPrompt('Manually exclude', p)
      : `Manually exclude ${p.count} record${p.count === 1 ? '' : 's'}?`;
    const note = window.prompt(
      `${head}\n\n`
      + 'They move to Excluded and stay there — no reprocess will bring them '
      + 'back. Use this when the punch should not be billed for a reason the '
      + "engine can't know. (Undo Exclusion reverses it.)\n\n"
      + 'Optional note (why):',
      '',
    );
    if (note === null) return;      // Cancel
    try {
      const res = await billingApi.markExcluded(selectionPayload(), note || null);
      const skipped = res.skipped_ineligible || 0;
      setMsg({
        type: skipped ? 'error' : 'success',
        text: `${res.updated.toLocaleString()} record${res.updated === 1 ? '' : 's'} manually excluded`
          + (skipped
            ? ` — ${skipped.toLocaleString()} skipped (only Pending and Gap records can be excluded).`
            : '.'),
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  const unexcludeSelected = () => runMutation('Undoing exclusion', async () => {
    if (selectionCount === 0) return;
    // Only MANUAL exclusions can be undone; the preview counts exactly those,
    // so on the Excluded tab the dialog says "412 of 8,900" and the engine-
    // derived rows are visibly left alone.
    const p = await bulkGate('unexclude', 'Undo Exclusion');
    if (!p) return;
    const tail = 'Each goes back to Pending or Gaps, whichever it came from.';
    if (!window.confirm(selectAllMatching
      ? bulkPrompt('Undo the manual exclusion on', p, tail)
      : `Undo the manual exclusion on ${p.count} record${p.count === 1 ? '' : 's'}? ${tail}`)) return;
    try {
      const res = await billingApi.unmarkExcluded(selectionPayload());
      const breakdown = Object.entries(res.by_status || {})
        .filter(([, n]) => n > 0)
        .map(([s, n]) => `${n} → ${STATUS_RETURN_LABEL[s] || s}`)
        .join(', ');
      setMsg({
        type: 'success',
        text: `${res.updated.toLocaleString()} exclusion${res.updated === 1 ? '' : 's'} undone`
          + (breakdown ? ` (${breakdown}).` : '.'),
      });
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  const pushSelected = () => runMutation('Syncing to NetSuite', async () => {
    if (selectionCount === 0) return;
    const p = await bulkGate('push', 'Sync to NetSuite');
    if (!p) return;
    if (selectAllMatching && !window.confirm(bulkPrompt(
      'Sync to NetSuite', p,
      'Each record is queued now and pushed by the background worker.',
    ))) return;
    try {
      const res = await pushQueueApi.pushRecords(selectionPayload());
      setMsg({
        type: 'success',
        text: `${res.queued.toLocaleString()} records queued for NetSuite sync`,
      });
      // Stay on the current tab; just refresh the list + clear the selection
      // so pushed rows fall off and the user can keep working.
      refreshList();
      clearSelection();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  });

  // Expanding a row is a place, not a preference: it pushes a history entry so
  // Back closes the panel, and the fetch is left to the effect above.
  const viewDetail = (r) => {
    const key = r.record_key || String(r.id);
    const open = String(expandedId) === String(r.record_key)
      || String(expandedId) === String(r.id);
    setExpandedId(open ? '' : key);
  };

  // A tab change drops everything that only made sense on the tab being left —
  // its gap chips, its exclusion reason, its sort, its open row — in one URL
  // write, and pushes a history entry so Back returns to the previous tab.
  const handleTabClick = (val) => {
    setParams({
      filter: val === 'all' ? null : val,
      gap_types: null,
      exclusion_reason: null,
      push_eligible: null,
      status_all: null,
      sort_by: null,
      sort_dir: null,
      open: null,
    }, { replace: false });
  };

  const toggleGapType = (g) => {
    const next = new Set(gapTypes);
    if (next.has(g)) next.delete(g); else next.add(g);
    setGapTypes(next);
  };

  // One write, so "Clear All" is a single history step and a single re-fetch
  // instead of eleven. The search box picks its empty draft back up from the
  // URL (see useUrlDebouncedParam).
  const clearAll = () => {
    setParams({
      q: null, date_start: null, date_end: null,
      site_code: null, billing_title_id: null, pay_group: null, customer_ot: null,
      gap_types: null, exclusion_reason: null, push_eligible: null, status_all: null,
    });
  };

  // For the All-tab action bar: which record statuses are currently selected?
  // Drives the one-button-at-a-time logic in RecordsTabs (Approve until all
  // selected are approved, then Push to NS).
  // In filter mode this is deliberately EMPTY: the selection spans records
  // that were never rendered, so loaded-row statuses would be a misleading
  // sample. RecordsTabs falls back to tab-derived actions instead.
  const selectedStatuses = useMemo(() => {
    const s = new Set();
    if (selectAllMatching) return s;
    for (const r of records) {
      if (selected.has(r.id) && r.status) s.add(r.status);
    }
    return s;
  }, [records, selected, selectAllMatching]);

  // Excluded tab mixes engine-derived and manual exclusions, and only the
  // manual ones can be undone. Status alone can't tell them apart — they all
  // carry status='excluded_ref_data' — so the action bar needs the reasons.
  // A NULL reason maps to '' so it can never pass the all-manual check.
  const selectedExclusionReasons = useMemo(() => {
    const s = new Set();
    if (selectAllMatching) return s;
    for (const r of records) {
      if (selected.has(r.id)) s.add(r.exclusion_reason || '');
    }
    return s;
  }, [records, selected, selectAllMatching]);

  return (
    <div>
      <div className="section-title">Billing Records ({total.toLocaleString()})</div>

      <RecordsTabs
        active={filter} onChange={handleTabClick}
        selectedCount={selectionCount}
        selectAllMatching={selectAllMatching}
        // On the All tab a filter-wide selection spans every status, so the
        // action bar needs the status the user narrowed to (if any) to know
        // what it may offer.
        filterStatus={statusAll}
        selectedStatuses={selectedStatuses}
        selectedExclusionReasons={selectedExclusionReasons}
        nsConflict={nsConflict}
        mutating={mutating}
        onApprove={() => updateStatus('approved')}
        onPush={pushSelected}
        onAcknowledge={acknowledgeSelected}
        onVoid={voidSelected}
        onRetry={retrySelected}
        onRepush={repushSelected}
        onMarkHandled={markHandledSelected}
        onUnhandle={unhandleSelected}
        onMarkExcluded={markExcludedSelected}
        onUnexclude={unexcludeSelected}
      />

      {filter !== 'push_queue' && (
        <RecordsFilters
          filter={filter}
          search={search} setSearch={setSearch}
          dateStart={dateStart} setDateStart={setDateStart}
          dateEnd={dateEnd}     setDateEnd={setDateEnd}
          siteCode={siteCode}             setSiteCode={setSiteCode}
          billingTitleId={billingTitleId} setBillingTitleId={setBillingTitleId}
          payGroup={payGroup}             setPayGroup={setPayGroup}
          customerOt={customerOt}         setCustomerOt={setCustomerOt}
          statusAll={statusAll}           setStatusAll={setStatusAll}
          gapTypes={gapTypes}             toggleGapType={toggleGapType}
          exclusionReason={exclusionReason} setExclusionReason={setExclusionReason}
          pushEligibleOnly={pushEligibleOnly} setPushEligibleOnly={setPushEligibleOnly}
          sortBy={sortBy} sortDir={sortDir} setSort={setSort}
          facets={facets} facetsLoading={facetsLoading}
          recordsShown={records.length}
          total={total}
          onClearAll={clearAll}
          onExport={exportRecords}
          exportLabel={exportLabel}
          exportDisabled={loading || total === 0}
        />
      )}

      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />

      {showQueue && <PushQueuePanel />}

      {filter !== 'push_queue' && (
        <>
          {filter === 'queued' && !loading && records.length > 0 && (
            <div className="alert alert-info" style={{
              fontSize: 13, padding: '10px 14px', marginBottom: 12,
              background: '#e7f0fb', color: '#0a4ea0', border: '1px solid #b8d4f0',
              borderRadius: 6,
            }}>
              ⏱ <strong>{records.length} record{records.length === 1 ? '' : 's'} queued.</strong>{' '}
              Sync to NetSuite is running in the background — these will move to{' '}
              <em>Synced</em> as each TimeBill is created. The daily 06:00 UTC timer
              also picks up anything still queued. Watch progress on the{' '}
              <em>Sync Queue</em> tab.
            </div>
          )}
          <RecordsTable
            filter={filter}
            records={records} total={total} hasMore={hasMore}
            loading={loading} loadingMore={loadingMore}
            selected={selected}
            selectAllMatching={selectAllMatching}
            selectionCount={selectionCount}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAll}
            onSelectAllMatching={selectAllMatchingFilter}
            onClearSelection={clearSelection}
            expandedId={expandedId} detailCache={detailCache}
            onExpand={viewDetail}
            sortBy={sortBy} sortDir={sortDir} setSort={setSort}
            sentinelRef={sentinelRef}
            selectable={
              filter === 'pending' ||
              filter === 'approved' ||
              filter === 'gap' ||
              filter === 'ops_review' ||
              filter === 'manually_handled' ||
              filter === 'excluded_ref_data' ||
              filter === 'pushed' ||
              filter === 'failed'
            }
          />
        </>
      )}
    </div>
  );
}
